const fetchAllOptions = () => {
    const queries = {
        names: new Parse.Query('Name')
            .include('taxonomy')
            .include('taxonomy.primarySystem'),
        events: new Parse.Query('Event')
            .include('type'),
        substantiations: new Parse.Query('Substantiation'),
        locDeterminization: new Parse.Query('LocDeterminization'),
        substrates: new Parse.Query('Substrate'),
        qualities: new Parse.Query('ObservationQuality'),
        locations: new Parse.Query('Location').include(['parent', 'town']),
        sourceOwneds: new Parse.Query('SourceOwned'),
        repositories: new Parse.Query('Repository'),
        noteTypes: new Parse.Query('NoteType'),
        evidenceFormats: new Parse.Query('EvidenceFormat'),
        subjects: new Parse.Query('Subject').limit(1000),
        towns: new Parse.Query('Town'),
        reproductionTypes: new Parse.Query('Reproduction'),
        nativityTypes: new Parse.Query('NativityType'),
        invasiveStatuses: new Parse.Query('InvasiveStatus'),
        securityTypes: new Parse.Query('SecurityType'),
        keywords: new Parse.Query('Keyword'),
        taxonomySystems: new Parse.Query('TaxonomySystem'),
        taxonomyTypes: new Parse.Query('TaxonomyType'),
        taxonomyCategories: new Parse.Query('TaxonomyCategory'),
        protectedStatuses: new Parse.Query('ProtectedStatus'),
        eventTypes: new Parse.Query('EventType'),
        sRanks: new Parse.Query('SRank'),
        rigcns: new Parse.Query('RIGCN'),
    };

  const promises = Object.entries(queries).map(([key, query]) => 
      query.find().then(results => ({[key]: results}))
  );

  return {
      type: 'FETCH_ALL_OPTIONS',
      payload: Promise.all(promises).then(results => 
          results.reduce((acc, curr) => ({...acc, ...curr}), {})
      )
  };
};

const fetchTableList = (tableName, { skip = 0, limit = 50, sortField = 'label', sortDirection = 'ascending', append = false, searchTerm = '', searchFields = [] } = {}) => {
    let query = new Parse.Query(tableName);
    
    if (tableName === 'Evidence') {
        query.include(['evidenceFormat', 'sourceOwned', 'subjects', 'createdBy']);
    } else if (tableName === 'Observation') {
        query.include(['name', 'name.taxonomy']);
    } else if (tableName === 'Event') {
        query.include(['type']);
    } else if (tableName === 'Location') {
        query.include(['parent', 'town']);
    }
    
    if (searchTerm && searchFields.length > 0) {
        const searchQueries = searchFields.map(field => {
            const fieldQuery = new Parse.Query(tableName);
            fieldQuery.matches(field, searchTerm, 'i');
            return fieldQuery;
        });
        query = Parse.Query.or(...searchQueries);
        
    }
    
    if (sortDirection === 'ascending') {
        query.ascending(sortField);
    } else {
        query.descending(sortField);
    }
    
    query.limit(limit);
    query.skip(skip);
    
    return {
        type: append ? 'APPEND_TABLE_LIST' : 'FETCH_TABLE_LIST',
        payload: Promise.all([
            query.find(),
            query.count()
        ]).then(async ([results, totalCount]) => {
            if (tableName === 'Person' && results.length > 0) {
                const expertiseQuery = new Parse.Query('PersonExpertise');
                expertiseQuery.containedIn('person', results);
                expertiseQuery.include(['subject']);
                const allExpertise = await expertiseQuery.find();
                
                const nameQuery = new Parse.Query('PersonName');
                nameQuery.containedIn('person', results);
                const allNames = await nameQuery.find();
                
                const expertiseByPerson = {};
                const namesByPerson = {};
                
                for (const exp of allExpertise) {
                    const personId = exp.get('person').id;
                    if (!expertiseByPerson[personId]) {
                        expertiseByPerson[personId] = [];
                    }
                    expertiseByPerson[personId].push(exp.get('subject'));
                }
                
                for (const name of allNames) {
                    const personId = name.get('person').id;
                    if (!namesByPerson[personId]) {
                        namesByPerson[personId] = [];
                    }
                    namesByPerson[personId].push(name.get('name'));
                }
                
                for (const person of results) {
                    const subjects = expertiseByPerson[person.id] || [];
                    person.set('subjects', subjects);
                    
                    const primaryName = `${person.get('firstName')} ${person.get('lastName')}`;
                    const alternativeNames = (namesByPerson[person.id] || [])
                        .filter(name => name !== primaryName);
                    person.set('alternativeNames', alternativeNames);
                }
            }
            
            return {
                results,
                totalCount,
                hasMore: skip + results.length < totalCount
            };
        })
    };
};

const save = (lookupTableData) => {
    const { className, objectId, ...dataToSave } = lookupTableData;
    
    return {
        type: `SAVE_${className.toUpperCase()}`,
        payload: (async () => {
            let lookupTableItem;
            
            if (objectId) {
                const query = new Parse.Query(className);
                lookupTableItem = await query.get(objectId);
            } else {
                lookupTableItem = new Parse.Object(className);
            }
            
            lookupTableItem.set('label', dataToSave.label);
            lookupTableItem.set('shortCode', dataToSave.shortCode);
            lookupTableItem.set('description', dataToSave.description);
            lookupTableItem.set('examples', dataToSave.examples);
            
            return lookupTableItem.save();
        })()
    };
};

const deleteRecord = (className, objectId) => {
    return {
        type: 'DELETE_RECORD',
        payload: (async () => {
            const query = new Parse.Query(className);
            const object = await query.get(objectId);
            await object.destroy();
            return { className, objectId };
        })()
    };
};

const searchLookupTable = (tableName, searchTerm, limit = 20, skip = 0) => {
    return {
        type: 'SEARCH_LOOKUP_TABLE',
        meta: { tableName },
        payload: (async () => {
            let query = new Parse.Query(tableName);
            
            if (tableName === 'Name') {
                query.include('taxonomy');
                query.include('taxonomy.primarySystem');
            } else if (tableName === 'PersonName') {
                query.include('person');
            }
            
            if (searchTerm && searchTerm.trim()) {
                if (tableName === 'PersonName') {
                    const nameQuery = new Parse.Query('PersonName');
                    nameQuery.matches('name', searchTerm, 'i');

                    const personFirstNameQuery = new Parse.Query('Person');
                    personFirstNameQuery.matches('firstName', searchTerm, 'i');
                    const personLastNameQuery = new Parse.Query('Person');
                    personLastNameQuery.matches('lastName', searchTerm, 'i');
                    const personOrQuery = Parse.Query.or(personFirstNameQuery, personLastNameQuery);

                    const personNameQuery = new Parse.Query('PersonName');
                    personNameQuery.matchesQuery('person', personOrQuery);

                    query = Parse.Query.or(nameQuery, personNameQuery);
                    query.include('person');
                } else if (tableName === 'Event') {
                    query.include('type');
                    query.matches('name', searchTerm, 'i');
                } else {
                    const searchField = tableName === 'Name' ? 'name' : 'label';
                    const finalSearchTerm = tableName === 'Location'
                        ? searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                        : searchTerm;
                    query.matches(searchField, finalSearchTerm, 'i');
                }
            }
            
            let orderField = 'label';
            if (tableName === 'Name') {
                orderField = 'name';
            } else if (tableName === 'PersonName') {
                orderField = 'name';
            }
            query.ascending(orderField);
            query.limit(limit);
            query.skip(skip);
            
            const [results, totalCount] = await Promise.all([
                query.find(),
                query.count()
            ]);
            
            return {
                tableName,
                results,
                totalCount,
                hasMore: skip + results.length < totalCount
            };
        })()
    };
};

const fetchSingleLookupTable = (tableName) => {
    return {
        type: 'FETCH_SINGLE_LOOKUP_TABLE',
        meta: { tableName },
        payload: (async () => {
            const query = new Parse.Query(tableName);
            query.limit(1000);
            const results = await query.find();
            return {
                tableName,
                results
            };
        })()
    };
};


export default {
    fetchAllOptions,
    fetchTableList,
    save,
    deleteRecord,
    searchLookupTable,
    fetchSingleLookupTable
};
